// PageHeader.tsx — the pattern that replaces `BotHeader` on every inherited screen (Task 7,
// handoff README §7 "Inherited screens — one pattern, nine screens"): an h1 with the section
// name, one subline of that section's own facts, and at most one primary action on the right.
// No back link, no bot name, no tab strip — the rail (Tasks 4-6) owns all three now.
import type { ReactNode } from 'react';
import { Skeleton } from '@repo/ui/components/skeleton';

export function PageHeader({
  title,
  subtitle,
  subtitleLoading,
  action,
}: {
  /** A plain section name on most screens; widened to `ReactNode` (review round 1,
   * source-detail-diagnostics Task 5) for the one case that needs a status chip inline beside
   * the title rather than as a separate fact — `SourceDetail.tsx`'s header names the source
   * itself, and its status is as much "the title" as the name is. */
  title: ReactNode;
  subtitle?: ReactNode;
  /** Subtitle-skeleton spec §C (2026-08-22) — true while the real subtitle fact is still
   * loading. Renders the placeholder INSIDE the exact same `<p class="mt-1 … text-[13px]">`
   * the real subtitle uses (never a sibling element with its own explicit height) — a caller
   * used to pass a raw `<Skeleton className="h-[15px] …" />` AS `subtitle` itself, which sat
   * in that same `<p>` but at a fixed 15px instead of the 13px/normal-line-height text box the
   * real sentence renders at (~19.5px): the header's height visibly shrank the instant data
   * arrived. `h-[1em]`/`inline-block`/`align-middle` on the Skeleton here sizes it to the `<p>`'s
   * own line box instead of a hardcoded pixel count, so the two states are structurally
   * identical — same element, same classes, same line height — and nothing shifts. Takes
   * priority over `subtitle` when both are passed (a loading screen's real value, if any, is
   * usually stale). */
  subtitleLoading?: boolean;
  action?: ReactNode;
}) {
  return (
    // Below `sm` the action row stacks under the title (2026-08-20): with two buttons in
    // the row, phone widths truncated even short titles ("Overview" -> "Ove...").
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <h1 className="font-display truncate text-2xl font-bold tracking-[-0.01em]">{title}</h1>
        {(subtitleLoading || subtitle) && (
          <p className="mt-1 truncate text-[13px] text-muted-foreground">
            {subtitleLoading ? (
              <Skeleton as="span" className="inline-block h-[1em] w-48 align-middle" />
            ) : (
              subtitle
            )}
          </p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
